import { useState, useEffect } from "react"
import { navigate } from "vike/client/router"
import Button from "aldoo-ra/Button"
import MenuGroup from "aldoo-ra/MenuGroup"
import PlatformAuth from "aldoo-ra/PlatformAuth"
import AccountButton from "./AccountButton"
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline"
import LogoIcon from "../Logo/LogoIcon"

// Auth-gated item — the Download entry only makes sense once signed in
// (installer lives in the account Downloads section). CMS defines it via
// options.action="downloads" rather than a link, since it has no CMS page.
const DOWNLOADS_ACTION = "downloads"
const DOWNLOADS_URL = "/account/downloads"

export default function NavbarMobile() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [auth] = PlatformAuth.useAuth()

  useEffect(() => setMounted(true), [])

  const closeMenu = () => setIsMenuOpen(false)

  useEffect(() => {
    const handleScroll = () => {
      // aldoo-ra/Modal locks body scroll via position:fixed while open,
      // which makes window.scrollY read 0 — skip so the scrolled
      // background doesn't flicker off behind an open modal.
      if (document.body.style.position === "fixed") return
      setIsScrolled(window.scrollY > 10)
    }
    handleScroll()
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  // Disable body scroll while the menu overlay is open
  useEffect(() => {
    if (isMenuOpen) {
      const originalStyle = window.getComputedStyle(document.body).overflow
      document.body.style.overflow = "hidden"
      return () => {
        document.body.style.overflow = originalStyle
      }
    }
  }, [isMenuOpen])

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 w-full z-50 transition-all duration-300 ${
          isMenuOpen || isScrolled
            ? "bg-paper/60 backdrop-blur-lg shadow-sm border-b border-border"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="h-16 px-4 flex items-center justify-between">
          {/* Logo (icon-only) + hamburger */}
          <div className="flex items-center gap-2">
            {/* <img
              src="/static/images/logos/app_logo.png"
              alt="Aldoo Snap"
              className="w-8 h-8 cursor-pointer"
              onClick={() => {
                closeMenu()
                navigate("/")
              }}
            /> */}

            <div
              className="w-10 flex-shrink-0 cursor-pointer"
              onClick={() => navigate("/")}
            >
              <LogoIcon />
            </div>

            <button
              className="p-2 -ml-1 rounded-lg text-text hover:text-primary transition-colors"
              onClick={() => setIsMenuOpen((open) => !open)}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <XMarkIcon className="w-6 h-6" /> : <Bars3Icon className="w-6 h-6" />}
            </button>
          </div>

          {/* CTA + account */}
          <div className="flex items-center gap-3">
            <MenuGroup
              name="BuyNow"
              renderAs="hlist"
              itemsAsLinks={false}
              styleOverrides={{
                menu: "flex flex-row items-center py-0 my-0",
                menu_item: "list-none px-0 py-0 my-0",
              }}
              menuItemRender={(item) => (
                <Button
                  variant="admin_primary"
                  onClick={() => navigate(item.link?.[0]?.url)}
                >
                  {item.label}
                </Button>
              )}
            />
            <AccountButton />
          </div>
        </div>
      </header>

      {/* Navigation menu overlay */}
      {isMenuOpen && (
        <div className="fixed top-16 inset-x-0 bottom-0 z-40 bg-paper/50 backdrop-blur-xl px-4 pt-6 flex flex-col gap-2 overflow-y-auto">
          <MenuGroup
            name="HeaderMenuCentral"
            renderAs="hlist"
            itemsAsLinks={false}
            styleOverrides={{
              menu: "flex flex-col gap-2 py-0 my-0 w-full",
              menu_item: "list-none px-0 py-0 my-0 w-full",
            }}
            menuItemRender={(item) => {
              const isDownloads = item.options?.action === DOWNLOADS_ACTION
              if (isDownloads && !(mounted && auth?.auth_token)) return null

              const url = isDownloads ? DOWNLOADS_URL : item.link?.[0]?.url

              return (
                <a
                  href={url}
                  onClick={closeMenu}
                  className="text-text hover:text-primary transition-colors font-medium py-3 px-4 bg-paper/50 border-b border-border rounded-2xl shadow-sm block w-full"
                >
                  {item.label}
                </a>
              )
            }}
          />
        </div>
      )}
    </>
  )
}